/**
 * @file Live tuning layer over the wave director (issue #26).
 *
 * {@link ./Waves.js WAVES} is frozen on purpose: it is the balance the build
 * ships with. This module is the editable copy of it — a flat map of overrides,
 * persisted in `localStorage`, that the debug window writes to and the wave
 * director reads through. With no overrides stored, the merged view equals
 * `WAVES` value for value, so a normal player is never affected.
 *
 * Adding a knob costs nothing here: every numeric field of `WAVES`, and every
 * archetype added to `costs`/`unlocks`/`weights`, appears in the merged view
 * and in {@link inspectWaveTuning} automatically. That is the extension point
 * the balancing sandbox is built on — new wave parameters become editable by
 * existing in `WAVES`, not by touching the UI.
 */

import { WAVES, computeWaveBudget, unlockedArchetypes } from './Waves.js';
import { ENEMIES } from './Enemies.js';
import { humanise } from '../utils/Text.js';

/** `localStorage` key holding the override map. */
export const WAVE_TUNING_STORAGE_KEY = 'spin.waveTuning.v1';

/** Sub-tables of `WAVES` keyed by archetype, rather than by tunable name. */
const TABLE_KEYS = Object.freeze(['costs', 'unlocks', 'weights']);

/**
 * Per-field editing rules. A field missing from the table still shows up — it
 * just falls back to {@link DEFAULT_META} — so the panel never has to be
 * updated in lockstep with `WAVES`.
 * @type {Readonly<Record<string, { label: string, step: number, min: number, integer?: boolean }>>}
 */
const SCALAR_META = Object.freeze({
  budgetBase: { label: 'Budget base', step: 0.5, min: 0.5 },
  budgetGrowth: { label: 'Budget growth', step: 0.01, min: 1 },
  budgetCap: { label: 'Budget cap', step: 1, min: 1 },
  firstWaveDelay: { label: 'First wave delay', step: 0.1, min: 0 },
  breather: { label: 'Breather', step: 0.1, min: 0 },
  spawnInterval: { label: 'Spawn interval', step: 0.01, min: 0.02 },
  telegraphTime: { label: 'Telegraph time', step: 0.05, min: 0 },
  maxAlive: { label: 'Max alive', step: 1, min: 1, integer: true },
  minPlayerDistance: { label: 'Min player distance', step: 1, min: 0 }
});

/** Editing rules for the archetype tables. */
const TABLE_META = Object.freeze({
  // A zero cost would let the budget loop spend forever, so the floor is real.
  costs: { label: 'Cost', step: 0.1, min: 0.1 },
  unlocks: { label: 'Unlock wave', step: 1, min: 1, integer: true },
  weights: { label: 'Weight', step: 1, min: 0 }
});

const DEFAULT_META = Object.freeze({ label: '', step: 0.1, min: 0 });

/**
 * Overrides only — never a full copy of `WAVES`. Keeping it sparse is what
 * makes "reset" a delete and what keeps saved balance readable.
 * @type {Record<string, number>}
 */
let overrides = {};

/** Memoised merge of `WAVES` + {@link overrides}. @type {object | null} */
let merged = null;

/** @type {Set<(tuning: object) => void>} */
const listeners = new Set();

/**
 * Storage backend. Swappable so tests — and any future sandbox harness — can
 * run the tuning layer without a DOM.
 * @type {Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null}
 */
let storage = detectStorage();

/**
 * @returns {Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null} The
 *   browser store, or null where there is none (tests, SSR, blocked cookies).
 */
function detectStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Safari throws instead of returning null when storage is blocked.
    return null;
  }
}

/**
 * Replaces the persistence backend.
 * @param {Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null} next Store to use.
 * @returns {void}
 */
export function setWaveTuningStorage(next) {
  storage = next;
}

/**
 * Merged, read-only view of the wave table. This is what the director reads.
 * @returns {typeof WAVES} `WAVES` with the current overrides applied.
 */
export function getWaveTuning() {
  if (!merged) merged = buildMerged();
  return /** @type {typeof WAVES} */ (merged);
}

/** @returns {object} A frozen merge of `WAVES` and {@link overrides}. */
function buildMerged() {
  /** @type {Record<string, any>} */
  const result = {};

  for (const [key, value] of Object.entries(WAVES)) {
    if (TABLE_KEYS.includes(key)) {
      /** @type {Record<string, number>} */
      const table = {};
      for (const archetype of Object.keys(value)) {
        table[archetype] = overrides[`${key}.${archetype}`] ?? value[archetype];
      }
      result[key] = Object.freeze(table);
      continue;
    }
    result[key] = typeof value === 'number' ? overrides[key] ?? value : value;
  }

  return Object.freeze(result);
}

/** @returns {void} Drops the memoised merge and notifies subscribers. */
function invalidate() {
  merged = null;
  const tuning = getWaveTuning();
  for (const listener of listeners) listener(tuning);
}

/**
 * @param {string} path Either a scalar name (`budgetBase`) or `table.archetype`.
 * @returns {{ label: string, step: number, min: number, integer?: boolean }} Editing rules.
 */
export function getFieldMeta(path) {
  const [head, tail] = path.split('.');
  if (tail !== undefined) return TABLE_META[head] ?? DEFAULT_META;
  return SCALAR_META[head] ?? { ...DEFAULT_META, label: humanise(head) };
}

/**
 * @param {string} path Field path.
 * @returns {number | undefined} The value shipped in the code, ignoring overrides.
 */
export function getDefaultValue(path) {
  const [head, tail] = path.split('.');
  const value = tail === undefined ? WAVES[head] : WAVES[head]?.[tail];
  return typeof value === 'number' ? value : undefined;
}

/**
 * Applies one edit. Takes effect on the next read by the director, which means
 * the next wave rolled — no reload, no restart.
 *
 * @param {string} path Field path.
 * @param {number | string} rawValue New value; strings come straight from an input.
 * @returns {boolean} False when the path is unknown or the value is not a finite number.
 */
export function setWaveValue(path, rawValue) {
  const fallback = getDefaultValue(path);
  if (fallback === undefined) return false;

  const parsed = typeof rawValue === 'number' ? rawValue : Number.parseFloat(String(rawValue));
  if (!Number.isFinite(parsed)) return false;

  const meta = getFieldMeta(path);
  let value = Math.max(meta.min, parsed);
  if (meta.integer) value = Math.round(value);

  // Storing a value equal to the default would persist noise and make a reset
  // to code defaults indistinguishable from an explicit edit.
  if (value === fallback) delete overrides[path];
  else overrides[path] = value;

  invalidate();
  return true;
}

/** @returns {Record<string, number>} A copy of the current override map. */
export function getWaveOverrides() {
  return { ...overrides };
}

/** @returns {boolean} True when anything differs from the shipped balance. */
export function hasWaveOverrides() {
  return Object.keys(overrides).length > 0;
}

/**
 * @param {string} path Field path.
 * @returns {boolean} True when this field has been edited away from the code default.
 */
export function isWaveValueOverridden(path) {
  return path in overrides;
}

/**
 * Replaces every override at once. Unknown or invalid entries are skipped, so a
 * stale saved payload can never break a build that renamed a field.
 *
 * @param {Record<string, number>} next Override map.
 * @returns {number} How many entries were applied.
 */
export function applyWaveOverrides(next) {
  overrides = {};
  let applied = 0;

  for (const [path, value] of Object.entries(next ?? {})) {
    const fallback = getDefaultValue(path);
    if (fallback === undefined || typeof value !== 'number' || !Number.isFinite(value)) continue;
    const meta = getFieldMeta(path);
    const clamped = meta.integer ? Math.round(Math.max(meta.min, value)) : Math.max(meta.min, value);
    if (clamped === fallback) continue;
    overrides[path] = clamped;
    applied++;
  }

  invalidate();
  return applied;
}

/**
 * Persists the overrides so they survive a page reload.
 * @returns {boolean} False when there is no writable store.
 */
export function saveWaveTuning() {
  if (!storage) return false;
  try {
    storage.setItem(WAVE_TUNING_STORAGE_KEY, JSON.stringify(overrides));
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads persisted overrides. Called once at boot, before the first wave rolls.
 * @returns {number} How many overrides were restored.
 */
export function loadWaveTuning() {
  if (!storage) return 0;
  try {
    const raw = storage.getItem(WAVE_TUNING_STORAGE_KEY);
    if (!raw) return 0;
    return applyWaveOverrides(JSON.parse(raw));
  } catch {
    // A corrupt payload must never keep the game from booting.
    return 0;
  }
}

/**
 * Discards every override, in memory and on disk, restoring the code defaults.
 * @returns {void}
 */
export function resetWaveTuning() {
  overrides = {};
  try {
    storage?.removeItem(WAVE_TUNING_STORAGE_KEY);
  } catch {
    // Nothing to do: the in-memory reset already happened.
  }
  invalidate();
}

/**
 * The current balance as pasteable JSON, shaped like the `WAVES` fields it
 * would replace, so tuned numbers can go straight back into the source table.
 *
 * @returns {string} Pretty-printed JSON.
 */
export function exportWaveTuning() {
  const tuning = getWaveTuning();
  /** @type {Record<string, any>} */
  const payload = {};
  for (const [key, value] of Object.entries(tuning)) {
    if (typeof value === 'number' || TABLE_KEYS.includes(key)) payload[key] = value;
  }
  return JSON.stringify({ overrides: getWaveOverrides(), waves: payload }, null, 2);
}

/**
 * Subscribes to edits.
 * @param {(tuning: object) => void} listener Called after every change.
 * @returns {() => void} Unsubscribe.
 */
export function onWaveTuningChanged(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * A render-ready snapshot of the whole tuning surface: what the director will
 * use for `wave`, plus the normalised pick share of every template.
 *
 * @param {number} wave Wave number the readout is relative to, 1-based.
 * @param {number} [levelIndex] Dungeon floor the readout is relative to, 1-based.
 * @returns {{
 *   wave: number,
 *   budget: number,
 *   scalars: Array<{ path: string, label: string, value: number, defaultValue: number, step: number, min: number, overridden: boolean }>,
 *   archetypes: Array<{ key: string, name: string, cost: number, unlock: number, weight: number, share: number, unlocked: boolean }>
 * }} Everything the debug window draws.
 */
export function inspectWaveTuning(wave, levelIndex = 1) {
  const tuning = getWaveTuning();
  const unlocked = new Set(unlockedArchetypes(wave, tuning, levelIndex));

  let totalWeight = 0;
  for (const key of unlocked) totalWeight += tuning.weights[key] ?? 1;

  /** @type {Array<any>} */
  const scalars = [];
  for (const [key, value] of Object.entries(tuning)) {
    if (typeof value !== 'number') continue;
    const meta = getFieldMeta(key);
    scalars.push({
      path: key,
      label: meta.label || humanise(key),
      value,
      defaultValue: /** @type {number} */ (getDefaultValue(key)),
      step: meta.step,
      min: meta.min,
      overridden: key in overrides
    });
  }

  const archetypes = Object.keys(tuning.costs).map((key) => {
    const weight = tuning.weights[key] ?? 1;
    const isUnlocked = unlocked.has(key);
    return {
      key,
      name: ENEMIES[key]?.name ?? humanise(key),
      cost: tuning.costs[key],
      unlock: tuning.unlocks[key] ?? 1,
      weight,
      // Locked templates are not in the draw, so they must not dilute the split.
      share: isUnlocked && totalWeight > 0 ? weight / totalWeight : 0,
      unlocked: isUnlocked
    };
  });

  return { wave, budget: computeWaveBudget(wave, tuning), scalars, archetypes };
}
